import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Sparkles, RefreshCw, TrendingUp, AlertTriangle, Repeat, Lightbulb, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { generateInsights } from '@flows.cash/core';
import type { AiInsightItem } from '@flows.cash/types';

const KIND_ICONS: Record<AiInsightItem['kind'], React.ReactNode> = {
  trend: <TrendingUp className="h-4 w-4" />,
  budget_pacing: <AlertTriangle className="h-4 w-4" />,
  subscription: <Repeat className="h-4 w-4" />,
  anomaly: <AlertTriangle className="h-4 w-4" />,
  suggestion: <Lightbulb className="h-4 w-4" />,
};

interface AiInsightsPanelProps {
  /** Compact mode caps the list at 3 items for the dashboard card. */
  compact?: boolean;
}

// AI-written analysis of the user's recent spending, generated server-side
// from SQL aggregates and cached per month+locale. Complements the rule-based
// InsightsWidget rather than replacing it.
export function AiInsightsPanel({ compact = false }: AiInsightsPanelProps) {
  const { t, i18n } = useTranslation('common');
  const queryClient = useQueryClient();
  const [regenerating, setRegenerating] = useState(false);

  const locale = (i18n.language || 'en').slice(0, 2);
  const periodKey = new Date().toISOString().slice(0, 7);
  const queryKey = ['ai-insights', periodKey, locale];

  const { data: response, isLoading } = useQuery({
    queryKey,
    queryFn: () => generateInsights({ locale }),
    staleTime: 60 * 60 * 1000, // server caches for 24h; don't refetch every mount
    retry: false,
  });

  const handleRegenerate = async () => {
    setRegenerating(true);
    const fresh = await generateInsights({ locale, forceRegenerate: true });
    queryClient.setQueryData(queryKey, fresh);
    setRegenerating(false);
  };

  const insights = response?.data;
  const errorCode = response?.error?.error;

  // A brand-new account has nothing to analyze; stay out of the way.
  if (errorCode === 'no_data') return null;

  const items = compact ? insights?.items.slice(0, 3) : insights?.items;

  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
      <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
        <div className="flex items-center">
          <Sparkles className="h-5 w-5 text-secondary dark:text-primary-dark mr-2 rtl:ml-2 rtl:mr-0" />
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {t('insights.ai.title', 'AI Insights')}
          </h3>
        </div>
        {insights && !compact && (
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark hover:bg-secondary/20 dark:hover:bg-primary-dark/20 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={cn('h-3 w-3', regenerating && 'animate-spin')} />
            {t('insights.ai.regenerate', 'Refresh')}
          </button>
        )}
      </div>

      <div className="p-6">
        {(isLoading || regenerating) && !insights && (
          <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('insights.ai.loading', 'Analyzing your spending…')}
          </div>
        )}

        {errorCode === 'rate_limited' && (
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {t('insights.ai.rateLimited', 'Too many refreshes — try again shortly.')}
          </p>
        )}
        {errorCode === 'insights_unavailable' && (
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {t('insights.ai.unavailable', "Insights aren't available right now — try again later.")}
          </p>
        )}

        {insights && (
          <>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
              {insights.summary}
            </p>
            <div className="space-y-3">
              {items?.map((item, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'p-4 rounded-lg border-l-4 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark',
                    item.severity === 'positive' && 'border-l-positive dark:border-l-positive-dark',
                    item.severity === 'warning' && 'border-l-negative dark:border-l-negative-dark',
                    item.severity === 'info' && 'border-l-secondary dark:border-l-primary-dark'
                  )}
                >
                  <div className="flex items-start space-x-3 rtl:space-x-reverse">
                    <div
                      className={cn(
                        'flex-shrink-0 p-1 rounded',
                        item.severity === 'positive' && 'text-positive dark:text-positive-dark',
                        item.severity === 'warning' && 'text-negative dark:text-negative-dark',
                        item.severity === 'info' && 'text-secondary dark:text-primary-dark'
                      )}
                    >
                      {KIND_ICONS[item.kind] ?? KIND_ICONS.trend}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {item.title}
                      </p>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-0.5">
                        {item.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-text-secondary/70 dark:text-text-dark-secondary/70">
              {t(
                'insights.ai.disclaimer',
                'Generated by AI from your transaction data — informational, not financial advice.'
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
